import { useCallback, useEffect, useRef, useState } from "react";

// How many px of scroll it takes to crossfade from fully expanded (identity
// row hidden) to fully collapsed (identity row shown, header pinned) -
// counted backward from the header's own sticky engagement point itself,
// not forward from a fixed distance off the hero's sentinel like the first
// version of this did. That version hand-tuned COLLAPSE_START_PX/RANGE to
// "roughly match .tournament-header-wrap's own resting height" for one
// particular hero - close enough there that it was hard to notice, but the
// sentinel sits right after the hero's own content, before the hero's own
// 18px bottom margin and the header's own sticky top:-1px offset, so on a
// tournament whose hero rendered any taller the identity row could already
// read fully opaque while .tournament-header-wrap itself still needed
// another 50-90px of scroll before the browser actually pinned it to the
// top - reading as the crest+title "appearing" well before the header
// visually locked into place, something ClubProfileModal/PlayerProfileModal
// don't have (their own compact-row fade is driven by the real measured
// hero height, not a hand-tuned guess). Measuring .tournament-header-wrap's
// own position directly instead ties the two together exactly, regardless
// of how tall any given tournament's hero happens to render.
const COLLAPSE_RANGE_PX = 60;

// Same progress-driven-by-scroll-position approach as useCompactToolbar
// (see its own comment for why: a boolean flag through a fixed-duration CSS
// transition can't track a continuous scroll gesture 1:1). Telegram-group
// style: the hero cover scrolls away in normal flow like any other content;
// Tournament.js passes the returned `progress` straight down to
// TournamentHeader.js as a prop (both live in the same tree now), which
// crossfades its crest+title in as this reaches 1.
export default function useTournamentHeroCollapse(enabled) {
    const heroSentinelRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const progressRef = useRef(0);
    const rafRef = useRef(null);

    const computeRawProgress = useCallback(() => {
        const wrap = document.querySelector(".tournament-header-wrap");
        if (!wrap) return 0;
        // Before the browser actually pins a position:sticky element, its
        // own getBoundingClientRect().top still reflects live normal-flow
        // position exactly like any other element, decreasing 1:1 with
        // scroll - once scroll carries it past the engagement point, this
        // just reads back the CSS top:-1px value itself and stays there
        // regardless of further scroll, which is correctly "fully
        // collapsed" too (no separate stuck/unstuck branch needed).
        const top = wrap.getBoundingClientRect().top;
        return Math.min(1, Math.max(0, (COLLAPSE_RANGE_PX - top) / COLLAPSE_RANGE_PX));
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const applyProgress = () => {
            const raw = computeRawProgress();
            if (progressRef.current === raw) return;
            progressRef.current = raw;
            setProgress(raw);
        };

        const onScroll = () => {
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                applyProgress();
            });
        };

        applyProgress();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [enabled, computeRawProgress]);

    return { heroSentinelRef, progress: enabled ? progress : 1 };
}
